import clsx from 'clsx';
import { motion } from 'framer-motion';
import React, { KeyboardEvent, useEffect, useRef, useState } from 'react';

import RenderIf from '@/components/Common/RenderIf';
import { Glow } from '@/components/ZAIChatLayout/ChatInput/Animation/Glow';
import ChatTextArea, {
  isSecondLineReached,
} from '@/components/ZAIChatLayout/ChatInput/Input';
import Icon from '@/components/ZAIChatLayout/ChatInput/Input/Icon';
import SubmitButton from '@/components/ZAIChatLayout/ChatInput/Input/SubmitButton';

interface ChatInputProps {
  messagesLength?: number;
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  disabled?: boolean;
  placeholder?: string;
  showDisclaimer?: boolean;
}

const KEYBOARD_THRESHOLD = 120;

const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  disabled,
  placeholder = 'Ask zAI',
  showDisclaimer = false,
  messagesLength,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [keyboardOffset, setKeyboardOffset] = useState(0);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const handleResize = () => {
      const heightDiff = window.innerHeight - vv.height;
      const isFocused = document.activeElement === textareaRef.current;

      if (heightDiff > KEYBOARD_THRESHOLD && isFocused) {
        setKeyboardOffset(heightDiff);
      } else {
        setKeyboardOffset(0);
      }
    };

    vv.addEventListener('resize', handleResize);
    return () => vv.removeEventListener('resize', handleResize);
  }, []);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && value.trim()) onSend();
    }
  };

  const isSecondLineInput = isSecondLineReached(value, textareaRef);
  return (
    <div
      style={{ paddingBottom: keyboardOffset }}
      className='sticky bottom-0 w-full z-10'
    >
      <div className='max-w-3xl w-full mx-auto pb-[env(safe-area-inset-bottom)] relative'>
        <motion.div
          className={clsx(
            'flex items-center pe-2 md:pb-2 pb-1.5 md:min-h-14 min-h-11 transition-all z-10',
            isSecondLineInput
              ? 'flex-col justify-between md:pt-4 pt-3 ps-4 gap-0 md:rounded-[25px] rounded-xl'
              : 'items-center md:pt-2 pt-1.5 md:ps-[25px]. ps-4 gap-2 rounded-[32px]',
            'bg-grayZl-950 border border-grayZl-900',
            'transition-colors',
            value && 'border-[#3A3A3B]',
          )}
        >
          <div
            className={clsx(
              'flex w-full gap-2 justify-between',
              isSecondLineInput ? 'items-start' : 'items-center',
            )}
          >
            {!isSecondLineInput && <Icon value={value} onChange={onChange} />}

            <ChatTextArea
              textareaRef={textareaRef}
              value={value}
              onChange={onChange}
              handleKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled}
            />

            {!isSecondLineInput && (
              <SubmitButton disabled={disabled} value={value} onSend={onSend} />
            )}
          </div>
          {isSecondLineInput && (
            <div className='flex justify-between items-center w-full'>
              <Icon value={value} onChange={onChange} />
              <SubmitButton disabled={disabled} value={value} onSend={onSend} />
            </div>
          )}
        </motion.div>
        {/* <div
          className='
            -z-10
    absolute
    left-0 bottom-0
    w-full h-[20px]
    bg-gradient-to-r
    from-[rgba(83,152,247,0.5)]
    via-[rgba(114,39,241,0.5)]
    to-[rgba(125,16,166,0.5)]
    blur-[17.5px]
mb-3
  '
        /> */}
        <Glow
          className=' -z-10 absolute
    left-0 bottom-0 w-full h-[20px] mb-3'
        />
        {showDisclaimer && (
          <div className='text-[10px] text-[#C7C6CF] text-center mt-1'>
            While this AI is highly capable, inaccuracies may occur
          </div>
        )}
      </div>
      <RenderIf condition={!!messagesLength}>
        <p className='text-white/50 text-[10px] leading-[18px] md:mt-2 mt-1 z-10 text-center'>
          While this Al is highly capable, inaccuracies may occur.
        </p>
      </RenderIf>
    </div>
  );
};

export default ChatInput;
